/**
 * useTagOrderPackages
 *
 * VRG-282 — the packages (boxes of tags) linked to one of the authenticated company's own
 * tag-shop orders:
 *   GET /shop/tags/orders/:id/packages → { data: Package[] }
 *
 * A COMPANY-scoped route, not the internal `GET /packages` that `usePackages` reads: a brand
 * has no access to the internal packages list, and this one is filtered to the caller's own
 * company server-side.
 *
 * NO shipment filter — a package is bindable whether or not the box has shipped (the whole
 * point is that certificates exist BEFORE it lands), so the list must not hide unshipped boxes.
 */
import { useQuery } from '@tanstack/react-query'
import { createApiCall } from 'services'

import { tagOrdersKeys } from './useTagOrders'
import type { PackageBoundProduct } from './usePackageProduct'
import type { PackageTagShopProduct } from './usePackages'
import type { ConsignmentDestination } from './usePackageConsignmentDestination'

// ─── Types ─────────────────────────────────────────────────────────────────────

export type TagOrderPackage = {
  id: string
  name: string
  /** Tags actually in the box. The count of certificates a binding would create. */
  quantity: number
  /**
   * The BRAND's product this box is pre-assigned to — not `tag_shop_product` below, which is
   * the tag SKU the box was purchased as. Null until bound.
   */
  product_id: string | null
  product: PackageBoundProduct | null
  /**
   * The tag SKU the box was purchased as. OPTIONAL because it is not in the endpoint's agreed
   * contract yet — the internal `GET /packages` list already returns exactly this projection,
   * so adding it here is a projection change, not new data.
   *
   * It is what `ProductSelectField`'s `defaultForTagId` compares against, so while it is absent
   * the picker loses its default-tag-first ordering and the set-as-default follow-up never
   * fires. `TagsOrderPage` derives it from the order's line items in the one case where that is
   * provably unambiguous; everything else needs the server.
   */
  tag_shop_product?: PackageTagShopProduct | null
  /**
   * VRG-455 — where this box physically went, and the control that decides
   * whether a buyer's scan may claim the certificates for the tags in it.
   * `unknown` is the default and leaves them claimable, which is right for
   * untracked fulfilment and wrong for stock the brand still holds.
   */
  consignment_destination: ConsignmentDestination
}

// ─── Defensive transform ───────────────────────────────────────────────────────

function transformPackage(raw: any): TagOrderPackage {
  return {
    id: String(raw?.id ?? ''),
    name: String(raw?.name ?? ''),
    quantity: Number(raw?.quantity ?? 0),
    product_id: raw?.product_id ?? null,
    product: raw?.product ?? null,
    // Coalesced to `unknown` rather than kept distinct from absent, unlike
    // `tag_shop_product` below: there is no client-side derivation to fall back
    // to, and "the server did not say" and "the server said unknown" call for
    // the same thing on screen — a box whose destination has not been recorded.
    consignment_destination:
      (raw?.consignment_destination as ConsignmentDestination) ?? 'unknown',
    // Undefined (field absent) and null (present, unset) are kept DISTINCT here: the page uses
    // the absent case to decide whether to attempt its own derivation.
    ...(raw?.tag_shop_product !== undefined
      ? { tag_shop_product: raw.tag_shop_product }
      : {}),
  }
}

// ─── useTagOrderPackages ──────────────────────────────────────────────────────

export function useTagOrderPackages(orderId: string | undefined) {
  const query = useQuery({
    queryKey: [...tagOrdersKeys.detail(orderId ?? ''), 'packages'] as const,
    queryFn: async (): Promise<TagOrderPackage[]> => {
      if (!orderId) return []
      const response = await createApiCall({
        method: 'GET',
        // Encoded for the same reason as the order-document route: a Shopify order id can
        // arrive as a full GID ("gid://shopify/Order/123"), whose slashes would split the path
        // and 404. A no-op for the numeric ids the list links with.
        url: `/shop/tags/orders/${encodeURIComponent(orderId)}/packages`,
      })
      const raw = response.data as any
      return (raw?.data ?? []).map(transformPackage)
    },
    enabled: Boolean(orderId),
    // Shorter than the order's own 2 minutes: a binding made in this modal must be visible on
    // the next refetch, and packages are also filled/linked by Vera staff while the brand watches.
    staleTime: 30 * 1000,
    retry: 1,
  })

  return {
    packages: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}
